/**
 * Operation Management - Main View
 *
 * Composes Stats, Filters, Table, and EditTaskModal
 * Matching Figma design 573:28047
 */

import { useState, useMemo, useCallback } from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import OperationStatsCards from '../components/OperationStatsCards';
import OperationFilters from '../components/OperationFilters';
import OperationTable from '../components/OperationTable';
import EditTaskModal from '../components/EditTaskModal';
import { useGetMaintenanceTasks } from '../services/operations.queries';
import type { IMaintenanceTaskListItem, IMaintenanceTaskStats } from '../types';

const OperationManagementView = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const isRtl = language === 'ar';

  // Filters state
  const [searchValue, setSearchValue] = useState('');
  const [taskTypeFilter, setTaskTypeFilter] = useState<string | undefined>();
  const [taskStatusFilter, setTaskStatusFilter] = useState<string | undefined>();
  const [priorityFilter, setPriorityFilter] = useState<string | undefined>();
  const [todayOnly, setTodayOnly] = useState(false);

  // Reset page when filters change
  const handleSearchChange = useCallback((v: string) => { setSearchValue(v); setActivePage(1); }, []);
  const handleTaskTypeChange = useCallback((v: string | undefined) => { setTaskTypeFilter(v); setActivePage(1); }, []);
  const handleTaskStatusChange = useCallback((v: string | undefined) => { setTaskStatusFilter(v); setActivePage(1); }, []);
  const handlePriorityChange = useCallback((v: string | undefined) => { setPriorityFilter(v); setActivePage(1); }, []);
  const handleTodayOnlyChange = useCallback((v: boolean) => { setTodayOnly(v); setActivePage(1); }, []);

  // Pagination
  const [activePage, setActivePage] = useState(1);
  const [perPage, setPerPage] = useState('10');

  // Modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<IMaintenanceTaskListItem | null>(null);

  // Query
  const { data, isLoading, refetch } = useGetMaintenanceTasks({
    search: searchValue || undefined,
    taskType: taskTypeFilter,
    taskStatus: taskStatusFilter,
    priority: priorityFilter,
    page: activePage,
    pageSize: Number(perPage),
  });

  const items: IMaintenanceTaskListItem[] = useMemo(() => {
    const result = data?.result;
    if (!result || !Array.isArray(result.tasks)) return [];
    let tasks = result.tasks;
    if (todayOnly) {
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      tasks = tasks.filter((t) => t.taskDateTime?.startsWith(todayStr));
    }
    return tasks;
  }, [data, todayOnly]);

  const totalCount = data?.result?.totalCount ?? 0;

  // Extract stats from API statistics response
  const stats: IMaintenanceTaskStats = useMemo(() => {
    const statistics = data?.result?.statistics;
    const pick = (item: { count: number; labelAr: string; labelEn: string } | undefined) => ({
      count: item?.count ?? 0,
      label: isRtl ? (item?.labelAr ?? '') : (item?.labelEn ?? ''),
    });
    return {
      scheduled: pick(statistics?.scheduled),
      assigned: pick(statistics?.assigned),
      pending: pick(statistics?.pending),
      inProgress: pick(statistics?.inProgress),
      completed: pick(statistics?.completed),
      cancelled: pick(statistics?.cancelled),
    };
  }, [data, isRtl]);

  // Handlers
  const handleEdit = useCallback((item: IMaintenanceTaskListItem) => {
    setSelectedTask(item);
    setEditModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setEditModalOpen(false);
    setSelectedTask(null);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setActivePage(page);
  }, []);

  const handlePerPageChange = useCallback((value: string) => {
    setPerPage(value);
    setActivePage(1);
  }, []);

  return (
    <Grid container spacing={2} >
      {/* Page Header — Figma: H6 Bold 20px title + Body Regular 16px subtitle */}
      <Grid item xs={12}>
        <Box sx={{ textAlign: 'start' }}>
          <Typography
            sx={{
              fontFamily: 'Tajawal, sans-serif',
              fontWeight: 700,
              fontSize: '20px',
              color: '#435363',
              lineHeight: '100%',
            }}
          >
            {t('OperationManagement.pageTitle')}
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Tajawal, sans-serif',
              fontWeight: 400,
              fontSize: '16px',
              color: '#ADBAC7',
              pt: '8px',
              lineHeight: '100%',
            }}
          >
            {t('OperationManagement.pageSubtitle')}
          </Typography>
        </Box>
      </Grid>

      {/* Stats Cards */}
      <Grid item xs={12}>
        <OperationStatsCards stats={stats} />
      </Grid>

      {/* Table Card — white rounded container */}
      <Grid item xs={12}>
        <Box
          sx={{
            backgroundColor: '#FFFFFF',
            borderRadius: '16px',
            border: '1px solid #EAEDF1',
            padding: '24px',
          }}
        >
          {/* Filters (includes toggle pill + search/filter row) */}
          <Box sx={{ mb: '16px' }}>
            <OperationFilters
              searchValue={searchValue}
              onSearchChange={handleSearchChange}
              taskTypeFilter={taskTypeFilter}
              onTaskTypeChange={handleTaskTypeChange}
              taskStatusFilter={taskStatusFilter}
              onTaskStatusChange={handleTaskStatusChange}
              priorityFilter={priorityFilter}
              onPriorityChange={handlePriorityChange}
              todayOnly={todayOnly}
              onTodayOnlyChange={handleTodayOnlyChange}
            />
          </Box>

          {/* Table */}
          <OperationTable
            items={items}
            totalCount={totalCount}
            isLoading={isLoading}
            activePage={activePage}
            perPage={perPage}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            onEdit={handleEdit}
          />
        </Box>
      </Grid>

      {/* Edit Modal — conditionally rendered so it remounts on every open, forcing a fresh GET-by-id call */}
      {editModalOpen && (
        <EditTaskModal
          open={true}
          onClose={handleCloseModal}
          task={selectedTask}
          onSuccess={() => {
            refetch();
          }}
        />
      )}
    </Grid>
  );
};

export default OperationManagementView;
